import axios from "axios";
import moment from "moment";
import { get } from 'lodash';
import {
  FETCH_CHART_DATA,
  LOADING_MODE,
  HANDLE_ERRORS,
} from "./types";
import BASE_URL from '../helpers/base-url';

import authorizeRequest from '../helpers/authorize-request';

const definePeriodParam = (start, end, force1h, force15m) => {
  let period;
  const currentDuration = end - start;
  const threeMonths = moment.duration({ months: 3 }).asMilliseconds();
  const twoMonths = moment.duration({ months: 2 }).asMilliseconds();
  const oneMonth = moment.duration({ weeks: 4 }).asMilliseconds();
  const twoWeeks = moment.duration({ weeks: 2 }).asMilliseconds();
  // const oneWeek = moment.duration({ weeks: 1 }).asMilliseconds();
  const threeDays = moment.duration({ days: 3 }).asMilliseconds();

  if (currentDuration > threeMonths) {
    period = "24h";
  } else if (currentDuration >= twoMonths) {
    period = "24h";
  } else if (currentDuration >= oneMonth) {
    period = "24h";
  } else if (currentDuration >= twoWeeks) {
    period = "1h";
  } else if (currentDuration >= threeDays) {
    period = "15m";
  } else if (force1h) {
    period = "1h";
  } else if (force15m) {
    period = "15m";
  } else {
    period = "30s";
  }
  return period;
};

export const getChartData = () => async (dispatch, getState) => {
  dispatch({ type: LOADING_MODE, payload: true });
  // declare parameters
  const { dateRange } = await getState();

  const { asset } = await getState();

  let start = dateRange.startDate.unix() * 1000;
  let end = dateRange.endDate.unix() * 1000;

  // make sure the time frame stays under 2 weeks max
  const currentDuration = Math.abs(end - start);

  const twoWeeks = moment.duration({ weeks: 2, days: 1 }).asMilliseconds();

  if (currentDuration > twoWeeks) {
    end = moment(end)
      .add(2, "w")
      .unix();

    dispatch({
      type: HANDLE_ERRORS,
      payload: {
        message: "Sorry, time frame too large, resized to two weeks",
        open: true
      }
    });
  }

  // define appropriate period parameter

  if (start === end) {
    end = dateRange.startDate.add(24, "hours").unix() * 1000;
  }

  const period = definePeriodParam(start, end);
  const period1h = definePeriodParam(start, end, true);
  const period15m = definePeriodParam(start, end, null, true);

  const pMaxValue = asset.peak_shaving_generic.p_max_kw;
  const pRefValue = asset.peak_shaving_generic.p_ref_kw;

  axios
    .all([
      // OLD battery data
      axios.get(
        `${BASE_URL}/api/external/battery/1?from=${start}&to=${end}&agg=avg&period=${period}`,
        authorizeRequest()
      ),
      // NEW battery data
      axios.get(
        `${BASE_URL}/api/external/metric/14?from=${start}&to=${end}&agg=avg&period=${period}`,
        authorizeRequest()
      ),

      // Generic Pmax data
      axios.get(
        `${BASE_URL}/api/external/metric/20?from=${start}&to=${end}&agg=max&period=${period1h}`,
        authorizeRequest()
      ),

      // Atypical Pmax on peak data
      axios.get(
        `${BASE_URL}/api/external/metric/21?from=${start}&to=${end}&agg=max&period=${period1h}`,
        authorizeRequest()
      ),

      // Atypical Pmax off peak data
      axios.get(
        `${BASE_URL}/api/external/metric/22?from=${start}&to=${end}&agg=max&period=${period1h}`,
        authorizeRequest()
      ),

      // meter with bess
      axios.get(
        `${BASE_URL}/api/external/meter/3?from=${start}&to=${end}&agg=avg&period=${period15m}`,
        authorizeRequest()
      ),
      // meter without bess
      axios.get(
        `${BASE_URL}/api/external/setpoint/12?from=${start}&to=${end}&agg=avg&period=${period15m}`,
        authorizeRequest()
      ),
      // get tariff info
      axios.get(
        `${BASE_URL}/api/external/tariff/1?from=${start}&to=${end}`,
        authorizeRequest()
      ),
      // Pmax load site w/o BESS (real)
      axios.get(
        `${BASE_URL}/api/external/metric/16?from=${start}&to=${end}&agg=max&period=${period1h}`,
        authorizeRequest()
      ),
      // Pmax load (real)
      axios.get(
        `${BASE_URL}/api/external/metric/17?from=${start}&to=${end}&agg=max&period=${period1h}`,
        authorizeRequest()
      ),
      // Setpoint
      axios.get(
        `${BASE_URL}/api/external/setpoint/11?from=${start}&to=${end}&agg=avg&period=${period}`,
        authorizeRequest()
      ),
      // battery bams
      axios.get(
        `${BASE_URL}/api/external/battery/2?from=${start}&to=${end}&agg=avg&period=${period}`,
        authorizeRequest()
      ),
    ])
    .then(axios.spread((
      battery,
      newBattery,
      pMax,
      pMaxOnPeak,
      pMaxOffPeak,
      meterWithBESS,
      meterWithoutBESS,
      tariffInfo,
      pMaxLoadWithoutBess,
      pMaxLoad,
      setpoint,
      batteryBams
      ) => ({
        data: {
          battery: battery.data.results,
          newBattery: newBattery.data.results,
          pMax: pMax.data.results,
          pMaxOnPeak: pMaxOnPeak.data.results,
          pMaxOffPeak: pMaxOffPeak.data.results,
          meter_with_bess: meterWithBESS.data.results,
          meter_without_bess: meterWithoutBESS.data.results,
          pMaxLoadWithoutBess: pMaxLoadWithoutBess.data.results,
          pMaxLoad: pMaxLoad.data.results,
          setpoint: setpoint.data.results,
          batteryBams: batteryBams.data.results
        },
        layout: {
          pMaxValue,
          pRefValue,
          tariff: tariffInfo.data.results || []
        }
      }))
    )
    .then(payload => {
      dispatch({ type: FETCH_CHART_DATA, payload });
      dispatch({ type: LOADING_MODE, payload: false });
    })
    .then(() => {
      dispatch({
        type: HANDLE_ERRORS,
        payload: { message: "", open: false }
      });
    })
    .catch(error => {
      const message = get(error, 'response.data.status_code') === 1200 ?
        'Interval request is longer than 168h (watch the time change)' :
        error.message;

      dispatch({
        type: HANDLE_ERRORS,
        payload: { message, open: true }
      });
      dispatch({ type: LOADING_MODE, payload: false });
    });
};



// WEBPACK FOOTER //
// ./src/actions/getChartData.js